import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AuthResponse, User } from '../types/auth';
import { Roles } from '../lib/constants';
import posthog from 'posthog-js';
import { isSecretFragmentPath } from '../lib/receiptPrivacy';
import { isSafeRedirectPath } from '../lib/utils';
import { applyApiHeaders } from '../API/config';
import {
  attachRestoreHandler,
  postToNativeShell,
  signalShellSignedOut,
} from './nativeBridge';
import { reportError } from '../lib/errorReporting';
import { releasePushToken } from './pushTokenBridge';
import { discardNotificationOpen } from '../../ParentApp/Shared/Contexts/notificationOpenBridge';

// The public receipt (/r#<secret>), claim (/claim#<token>) and password reset (/password-reset#<token>) pages need no session, and a logout
// redirect from it would drop the secret in the fragment — so a stale or
// cleared session never navigates away from it.
const onReceiptPage = () => isSecretFragmentPath(window.location.pathname ?? '');

interface UserContextType {
  user: User | null;
  token: string | null;
  tokenExpiry: number | null;
  nextAction: string | null;
  studentNumber: string | null;
  isAuthenticated: boolean;
  hasPermissions: (roles: string[]) => boolean;
  authenticate: (data: AuthResponse) => void;
  clearNextAction: () => void;
  // Overloaded so existing `onClick={logout}` call sites keep type-checking.
  logout: {
    (): void;
    (options: { returnTo?: string }): void;
  };
  logoutParent: () => void;
  logoutStudent: () => void;
  setSelectedChild: (studentNumber: string) => void;
}

interface RestorePayload {
  token: string;
  user: User;
  expiresAt: number;
}

const parseRestorePayload = (payload: unknown): RestorePayload | null => {
  if (!payload || typeof payload !== 'object') return null;
  const { token, user, expiresAt } = payload as Record<string, unknown>;
  if (typeof token !== 'string' || token.length === 0) return null;
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) return null;
  if (!user || typeof user !== 'object' || Array.isArray(user)) return null;
  return { token, user: user as User, expiresAt };
};

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem('token')
  );

  const [nextAction, setNextAction] = useState<string | null>(() =>
    localStorage.getItem('nextAction')
  );

  const [tokenExpiry, setTokenExpiry] = useState<number | null>(() => {
    const storedExpiry = localStorage.getItem('tokenExpiry');
    return storedExpiry ? parseInt(storedExpiry, 10) : null;
  });

  const [studentNumber, setStudentNumber] = useState<string | null>(() =>
    localStorage.getItem('studentNumber')
  );

  const isAuthenticated = !!user && !!token;

  // Bumped whenever the session changes hands (login/logout) so an in-flight
  // native restore can't land on top of it.
  const sessionGenerationRef = useRef(0);
  const restoreAttemptedRef = useRef(false);
  const hasSessionRef = useRef(isAuthenticated);
  hasSessionRef.current = isAuthenticated;

  const persistSession = useCallback(
    (sessionToken: string, sessionUser: User, expiry: number) => {
      setUser(sessionUser);
      setToken(sessionToken);
      setTokenExpiry(expiry);
      localStorage.setItem('user', JSON.stringify(sessionUser));
      localStorage.setItem('token', sessionToken);
      localStorage.setItem('tokenExpiry', expiry.toString());
    },
    []
  );

  const hasPermissions = useCallback(
    (roles: string[]) => {
      if (!user?.roles) return false;
      return roles.some(role => user.roles.includes(role));
    },
    [user?.roles]
  );

  const clearNextAction = useCallback(() => {
    setNextAction(null);
    localStorage.removeItem('nextAction');
  }, []);

  const logout = useCallback((options?: { returnTo?: string }) => {
    if (
      user?.roles?.includes(Roles.PARENT) &&
      Number(localStorage.getItem('tokenExpiry')) > Date.now()
    ) {
      releasePushToken();
    }
    sessionGenerationRef.current += 1;
    discardNotificationOpen();
    setUser(null);
    setToken(null);
    setTokenExpiry(null);
    localStorage.clear();
    signalShellSignedOut();
    posthog.reset();
    const returnTo = options?.returnTo;
    window.location.href = isSafeRedirectPath(returnTo) ? returnTo : `/login`;
  }, [user?.roles]);

  const logoutStudent = useCallback(() => {
    sessionGenerationRef.current += 1;
    discardNotificationOpen();
    setUser(null);
    setToken(null);
    setTokenExpiry(null);
    localStorage.clear();
    signalShellSignedOut();
    posthog.reset();
    window.location.replace('/student/login');
  }, []);

  const logoutParent = useCallback(() => {
    // Only an explicit sign-out releases the phone. An expired session keeps its
    // pushes: the parent still wants them on their own phone, and a lapsed
    // session could not authorise the release anyway.
    if (Number(localStorage.getItem('tokenExpiry')) > Date.now()) {
      releasePushToken();
    }
    sessionGenerationRef.current += 1;
    discardNotificationOpen();
    setUser(null);
    setToken(null);
    setTokenExpiry(null);
    // Explicitly clear student number to ensure parent can login with different child
    setStudentNumber(null);
    localStorage.removeItem('studentNumber');
    localStorage.removeItem('sessionId');
    localStorage.clear();
    signalShellSignedOut();
    posthog.reset();
    window.location.href = `/parent/login`;
  }, []);

  const setSelectedChild = useCallback((newStudentNumber: string) => {
    setStudentNumber(newStudentNumber);
    localStorage.setItem('studentNumber', newStudentNumber);
  }, []);

  const authenticate = useCallback(
    (data: AuthResponse) => {
      if (!data?.token?.accessToken) return;

      sessionGenerationRef.current += 1;
      discardNotificationOpen();
      const expiryTime = Date.now() + data.token.expiresIn * 1000;

      persistSession(data.token.accessToken, data.user, expiryTime);
      setNextAction(data.nextAction);
      localStorage.setItem('nextAction', data.nextAction);

      if (data.user?.roles?.includes(Roles.PARENT)) {
        postToNativeShell({
          type: 'SAVE_TOKEN',
          token: data.token.accessToken,
          user: data.user,
          expiresAt: expiryTime,
        });
      }

      posthog.identify(data.user.id.toString(), {
        name: data.user.firstName,
        school: data.user.school?.displayName,
      });
    },
    [persistSession]
  );

  // Native shell session restore (biometric/passcode unlock). The payload's
  // identity is never trusted: the candidate token is checked against the
  // server before anything is stored, and only a parent session is accepted.
  useEffect(() => {
    const restore = async (raw: unknown) => {
      if (restoreAttemptedRef.current) return;
      restoreAttemptedRef.current = true;

      const payload = parseRestorePayload(raw);
      if (!payload || hasSessionRef.current) return;
      if (payload.expiresAt <= Date.now()) return;

      const generation = sessionGenerationRef.current;
      try {
        // Not authenticatedFetch/RTK Query: both read the token from
        // localStorage, and the candidate must not be stored until the server
        // confirms it. A 401 here is an expected outcome, not an error. No
        // cookies: a lingering server session must not vouch for a dead token.
        // eslint-disable-next-line no-restricted-syntax
        const res = await fetch('/api/auth/profile', {
          headers: applyApiHeaders(new Headers(), payload.token),
          credentials: 'omit',
        });
        const isStale = () =>
          generation !== sessionGenerationRef.current || hasSessionRef.current;
        // The server has ruled this saved session out for good, so stop the
        // shell offering it (and re-prompting the unlock) on every cold start.
        const rejectSavedSession = () => postToNativeShell({ type: 'REMOVE_TOKEN' });

        if (isStale()) return;
        if (res.status === 401 || res.status === 403) {
          rejectSavedSession();
          return;
        }
        if (!res.ok) return;
        const profile = await res.json();
        if (isStale()) return;
        if (
          profile?.id !== payload.user.id ||
          !Array.isArray(profile.roles) ||
          !profile.roles.includes(Roles.PARENT)
        ) {
          rejectSavedSession();
          return;
        }

        // The profile carries no school, so the saved one is kept for display;
        // the server never reads it. Everything else is the server's.
        const restoredUser: User = { school: payload.user.school, ...profile };
        persistSession(payload.token, restoredUser, payload.expiresAt);
      } catch (error) {
        reportError(error, {
          where: 'UserContext.nativeRestore',
          level: 'warning',
        });
      }
    };

    return attachRestoreHandler(raw => {
      void restore(raw);
    });
  }, [persistSession]);

  // Token expiry handling
  useEffect(() => {
    if (!tokenExpiry || onReceiptPage()) return;

    const checkExpiry = () => {
      const now = Date.now();
      if (tokenExpiry - now <= 0) {
        if (user?.roles?.includes(Roles.PARENT)) {
          logoutParent();
        } else {
          logout();
        }
      }
    };

    // Check immediately on mount/update
    checkExpiry();

    // Check every minute instead of using a single long-running timeout
    // a single timeout can overflow if it's longer than ~24 days
    const interval = setInterval(checkExpiry, 60000);

    return () => clearInterval(interval);
  }, [tokenExpiry, logout, logoutParent, user?.roles]);

  // Cross-tab sync: a login/logout/child-switch in one tab writes
  // localStorage directly, which never touches this tab's React state on
  // its own -- only the native `storage` event (fired in every *other* tab
  // of the origin) tells us to catch up.
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Real browsers always set storageArea for both localStorage and
      // sessionStorage events -- reject anything that's explicitly a
      // different storage area (e.g. a future sessionStorage user). Leave
      // events that omit it (some non-browser/test dispatchers can't
      // construct a real Storage instance to compare against) untouched.
      if (event.storageArea && event.storageArea !== localStorage) return;

      if (event.key === null) {
        // localStorage.clear() elsewhere (logout)
        setUser(null);
        setToken(null);
        setTokenExpiry(null);
        setNextAction(null);
        setStudentNumber(null);
        if (onReceiptPage()) return;
        window.location.href = user?.roles?.includes(Roles.PARENT)
          ? '/parent/login'
          : '/login';
        return;
      }

      switch (event.key) {
        case 'user':
          setUser(event.newValue ? JSON.parse(event.newValue) : null);
          break;
        case 'token':
          setToken(event.newValue);
          break;
        case 'tokenExpiry':
          setTokenExpiry(event.newValue ? parseInt(event.newValue, 10) : null);
          break;
        case 'nextAction':
          setNextAction(event.newValue);
          break;
        case 'studentNumber':
          setStudentNumber(event.newValue);
          break;
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user?.roles]);

  const value: UserContextType = {
    user,
    token,
    tokenExpiry,
    nextAction,
    studentNumber,
    isAuthenticated,
    hasPermissions,
    authenticate,
    clearNextAction,
    logout,
    logoutParent,
    logoutStudent,
    setSelectedChild,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook to use the user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Backward compatibility hook
export const useAuth = (): UserContextType => {
  return useUser();
};
